import Loader from 'components/ui/LoadingIndicator';
import ContainerBox from 'components/ui/ContainerBox';
import React, { useState } from 'react';

import { ChannelGroup } from '../../../shared/types';
import ManageTemplateDialog from '../../ui/ManageTemplateDialog';
import {
    MessageTypes,
    useNotification,
} from '../../providers/NotificationProvider';
import { sanitizeRouteId } from '../../../utils/route-params.utils';

import { useMutation, useQuery, useApolloClient } from '@apollo/react-hooks';
import { useParams } from 'react-router-dom';
import {
    useFormControl,
    useFormGroup,
    useManageTemplateForm,
} from '../../../utils/form.utils';
import { requiredValidator } from '../../../utils/validator.utils';
import ManageTemplate from '../../ui/ManageTemplate';
import { ManageTemplateInstance } from '../../ui/ManageTemplate';
import FormTextField from '../../ui/form/FormTextField';
import FormTextArea from '../../ui/form/FormTextArea';
import {
    ADD_CHANNEL_GROUP,
    DELETE_CHANNEL_GROUP,
    EDIT_CHANNEL_GROUP,
    GET_CHANNEL_GROUP_BY_ID,
    GET_CHANNEL_GROUPS,
} from '../../../gql/channelGroups';
import { baseUrls } from '../../../utils/baseUrls.utils';
import { Box, Button } from '@mui/material';
const returnListUrl = baseUrls.publisher.channelGroups.list;

const ChannelGroupAdd = (): JSX.Element => {
    const { showMessage } = useNotification();
    const apolloClient = useApolloClient();
    const { _id: routeId } = useParams<{ _id?: string }>();
    const _id = sanitizeRouteId(routeId);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

    const integerValidator = (value: string) => ({
        valid: /^-?\d+$/.test((value || '').trim()),
        message: 'Weight should be an integer',
    });
    const form = useFormGroup({
        name: useFormControl<string>('', [requiredValidator]),
        description: useFormControl<string>('', []),
        weight: useFormControl<string>('0', [
            requiredValidator,
            integerValidator,
        ]),
    });

    const { loading, data } = useQuery(GET_CHANNEL_GROUP_BY_ID, {
        skip: !_id,
        variables: { id: _id },
        fetchPolicy: 'network-only',
        onCompleted: (response: { findChannelGroupById: ChannelGroup }) => {
            const editItem = response?.findChannelGroupById;
            if (!editItem) {
                return;
            }
            const { name, description, weight } = form;
            name.setInitialValue(editItem.name);
            description.setInitialValue(editItem.description || '');
            weight.setInitialValue(String(editItem.weight ?? 0));
        },
    });

    const editItem: ChannelGroup | null = data?.findChannelGroupById || null;
    const [deleteGroup, { loading: deleting }] = useMutation(
        DELETE_CHANNEL_GROUP,
        {
            refetchQueries: [{ query: GET_CHANNEL_GROUPS }],
        }
    );

    const {
        isNew,
        formMessage,
        formBusy,
        onSubmit,
        onCancel,
        onAlertBoxClose,
    } = useManageTemplateForm(
        ManageTemplateInstance.ChannelGroup,
        form,
        editItem,
        ADD_CHANNEL_GROUP,
        EDIT_CHANNEL_GROUP,
        null,
        returnListUrl
    );

    const refetchGroupsQuery = async () => {
        try {
            await apolloClient.query({
                query: GET_CHANNEL_GROUPS,
                fetchPolicy: 'network-only',
            });
        } catch (error) {
            showMessage(
                'Failed to refresh groups. Refresh page to try again',
                MessageTypes.error
            );
        }
    };

    const handleSubmit = async (ev: React.SyntheticEvent) => {
        await onSubmit(ev, {
            ...form.value,
            weight: parseInt(form.weight.value, 10),
            version: editItem?.version,
        });
        await refetchGroupsQuery();
    };

    const closeDeleteDialog = () => {
        setDeleteDialogOpen(false);
    };

    const onConfirmDelete = async () => {
        if (!_id) return;

        try {
            await deleteGroup({ variables: { id: _id } });
            showMessage(
                `The channel group ${form.name.value} has been removed.`,
                MessageTypes.info
            );
            await refetchGroupsQuery();
            onCancel();
        } catch (error) {
            showMessage(
                'Failed to delete group. Please try again',
                MessageTypes.error
            );
        }
    };

    if (loading) {
        return <Loader />;
    }

    return (
        <>
            <ContainerBox>
                <Box sx={{ flex: 1, flexDirection: 'column', maxWidth: 1200 }}>
                    <ManageTemplate
                        pathArea={'Channel Groups'}
                        returnUrl={returnListUrl}
                        templateInstance={ManageTemplateInstance.ChannelGroup}
                        isNew={isNew}
                        formMessage={formMessage}
                        formBusy={formBusy}
                        onCancel={onCancel}
                        onSubmit={handleSubmit}
                        onAlertBoxClose={onAlertBoxClose}
                        showDelete={false}
                        templateCreateButtonLabel="Add new group"
                        templateUpdateButtonLabel="Save changes"
                        rightAction={
                            !isNew ? (
                                <Button
                                    id="channel-group-delete-button"
                                    variant="contained"
                                    color="error"
                                    onClick={() => setDeleteDialogOpen(true)}
                                >
                                    Delete group
                                </Button>
                            ) : undefined
                        }
                    >
                        <FormTextField
                            id="channel-group-name-input"
                            label="Name"
                            field={form.name}
                            charsLimit={50}
                        />
                        <FormTextField
                            id="channel-group-weight-input"
                            label="Position"
                            field={form.weight}
                            charsLimit={10}
                        />
                        <FormTextArea
                            id={'channel-group-description-textarea'}
                            field={form.description}
                            charsLimit={500}
                        />
                    </ManageTemplate>
                </Box>
            </ContainerBox>
            <ManageTemplateDialog
                dialogOpenState={deleteDialogOpen}
                closeDialog={closeDeleteDialog}
                title="Remove channel group"
                dialogMessage={`Are you sure you want to remove "${form.name.value}"?`}
                onDelete={onConfirmDelete}
                submitBtnText="Remove"
                submitBtnColorType="error"
                submitBtnDisabled={deleting}
            />
        </>
    );
};

export default ChannelGroupAdd;
